import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CustomerListModal = ({ isOpen, onClose, agentId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Mock customer data
  const mockCustomers = [
    {
      id: 'cust_001',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@email.com',
      phone: '+1 (555) 123-4567',
      status: 'active',
      kycStatus: 'verified',
      registeredDate: '2024-10-15',
      totalTransactions: 23,
      totalVolume: 12450,
      lastTransactionDate: '2024-11-18',
      country: 'United States',
      riskLevel: 'low'
    },
    {
      id: 'cust_002',
      name: 'Michael Chen',
      email: 'michael.chen@email.com',
      phone: '+1 (555) 234-5678',
      status: 'active',
      kycStatus: 'verified',
      registeredDate: '2024-09-22',
      totalTransactions: 45,
      totalVolume: 28750,
      lastTransactionDate: '2024-11-19',
      country: 'Canada',
      riskLevel: 'low'
    },
    {
      id: 'cust_003',
      name: 'Elena Rodriguez',
      email: 'elena.rodriguez@email.com',
      phone: '+34 612 345 678',
      status: 'active',
      kycStatus: 'pending',
      registeredDate: '2024-11-10',
      totalTransactions: 5,
      totalVolume: 2100,
      lastTransactionDate: '2024-11-16',
      country: 'Spain',
      riskLevel: 'medium'
    },
    {
      id: 'cust_004',
      name: 'James Wilson',
      email: 'james.wilson@email.com',
      phone: '+44 20 1234 5678',
      status: 'inactive',
      kycStatus: 'verified',
      registeredDate: '2024-07-05',
      totalTransactions: 12,
      totalVolume: 8900,
      lastTransactionDate: '2024-09-30',
      country: 'United Kingdom',
      riskLevel: 'low'
    },
    {
      id: 'cust_005',
      name: 'Fatima Al-Rashid',
      email: 'fatima.rashid@email.com',
      phone: '+971 50 123 4567',
      status: 'active',
      kycStatus: 'verified',
      registeredDate: '2024-08-12',
      totalTransactions: 67,
      totalVolume: 45320,
      lastTransactionDate: '2024-11-20',
      country: 'United Arab Emirates',
      riskLevel: 'low'
    },
    {
      id: 'cust_006',
      name: 'David Kim',
      email: 'david.kim@email.com',
      phone: '+82 10 1234 5678',
      status: 'active',
      kycStatus: 'verified',
      registeredDate: '2024-10-01',
      totalTransactions: 18,
      totalVolume: 9870,
      lastTransactionDate: '2024-11-17',
      country: 'South Korea',
      riskLevel: 'low'
    },
    {
      id: 'cust_007',
      name: 'Maria Santos',
      email: 'maria.santos@email.com',
      phone: '+52 55 1234 5678',
      status: 'suspended',
      kycStatus: 'under_review',
      registeredDate: '2024-11-01',
      totalTransactions: 3,
      totalVolume: 1500,
      lastTransactionDate: '2024-11-10',
      country: 'Mexico',
      riskLevel: 'high'
    },
    {
      id: 'cust_008',
      name: 'Ahmed Hassan',
      email: 'ahmed.hassan@email.com',
      phone: '+20 100 123 4567',
      status: 'active',
      kycStatus: 'verified',
      registeredDate: '2024-06-20',
      totalTransactions: 89,
      totalVolume: 67540,
      lastTransactionDate: '2024-11-19',
      country: 'Egypt',
      riskLevel: 'low'
    }
  ];

  const filteredCustomers = mockCustomers?.filter(customer => {
    const matchesSearch = customer?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         customer?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         customer?.phone?.includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || customer?.status === filterStatus;
    return matchesSearch && matchesStatus;
  })?.sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        return new Date(b?.lastTransactionDate) - new Date(a?.lastTransactionDate);
      case 'name':
        return a?.name?.localeCompare(b?.name);
      case 'volume':
        return b?.totalVolume - a?.totalVolume;
      case 'transactions':
        return b?.totalTransactions - a?.totalTransactions;
      default:
        return 0;
    }
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-success/10 text-success border-success/20',
      inactive: 'bg-muted text-muted-foreground border-border',
      suspended: 'bg-error/10 text-error border-error/20'
    };
    return styles?.[status] || styles?.inactive;
  };

  const getKycBadge = (kycStatus) => {
    const styles = {
      verified: 'bg-success/10 text-success border-success/20',
      pending: 'bg-warning/10 text-warning border-warning/20',
      under_review: 'bg-info/10 text-info border-info/20',
      rejected: 'bg-error/10 text-error border-error/20'
    };
    return styles?.[kycStatus] || styles?.pending;
  };

  const getRiskBadge = (riskLevel) => {
    const styles = {
      low: 'bg-success/10 text-success',
      medium: 'bg-warning/10 text-warning',
      high: 'bg-error/10 text-error'
    };
    return styles?.[riskLevel] || styles?.low;
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleCloseDetails = () => {
    setSelectedCustomer(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Customer List</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage and view all your registered customers
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Filters and Search */}
        <div className="p-6 border-b border-border space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                icon="Search"
              />
            </div>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              options={[
                { value: 'all', label: 'All Customers' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'suspended', label: 'Suspended' }
              ]}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {filteredCustomers?.length} of {mockCustomers?.length} customers
            </div>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              options={[
                { value: 'recent', label: 'Recent Activity' },
                { value: 'name', label: 'Name (A-Z)' },
                { value: 'volume', label: 'Transaction Volume' },
                { value: 'transactions', label: 'Transaction Count' }
              ]}
            />
          </div>
        </div>

        {/* Customer List */}
        <div className="flex-1 overflow-auto p-6">
          {selectedCustomer ? (
            // Customer Details View
            (<div className="space-y-6">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={handleCloseDetails}
                  className="flex items-center space-x-2"
                >
                  <Icon name="ArrowLeft" size={16} />
                  <span>Back to List</span>
                </Button>
              </div>
              <div className="bg-muted/50 rounded-lg p-6 space-y-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <h3 className="text-2xl font-semibold text-foreground">
                      {selectedCustomer?.name}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadge(selectedCustomer?.status)}`}>
                        {selectedCustomer?.status?.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getKycBadge(selectedCustomer?.kycStatus)}`}>
                        KYC: {selectedCustomer?.kycStatus?.replace('_', ' ')?.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskBadge(selectedCustomer?.riskLevel)}`}>
                        Risk: {selectedCustomer?.riskLevel?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-foreground">Contact Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Icon name="Mail" size={16} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{selectedCustomer?.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="Phone" size={16} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{selectedCustomer?.phone}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="MapPin" size={16} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{selectedCustomer?.country}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-foreground">Account Details</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Icon name="Calendar" size={16} className="text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Registered:</span>
                        <span className="text-sm text-foreground">
                          {new Date(selectedCustomer?.registeredDate)?.toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="Activity" size={16} className="text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Last Transaction:</span>
                        <span className="text-sm text-foreground">
                          {new Date(selectedCustomer?.lastTransactionDate)?.toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-border">
                  <div className="bg-background rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Total Transactions</span>
                      <Icon name="TrendingUp" size={16} className="text-primary" />
                    </div>
                    <div className="text-2xl font-bold text-foreground">
                      {selectedCustomer?.totalTransactions}
                    </div>
                  </div>
                  <div className="bg-background rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Total Volume</span>
                      <Icon name="DollarSign" size={16} className="text-success" />
                    </div>
                    <div className="text-2xl font-bold text-foreground">
                      ${selectedCustomer?.totalVolume?.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-background rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Avg. Transaction</span>
                      <Icon name="BarChart3" size={16} className="text-info" />
                    </div>
                    <div className="text-2xl font-bold text-foreground">
                      ${Math.round(selectedCustomer?.totalVolume / selectedCustomer?.totalTransactions)?.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <Button variant="outline">View Transactions</Button>
                <Button variant="outline">Send Message</Button>
                <Button variant="primary">Initiate Transfer</Button>
              </div>
            </div>)
          ) : (
            // Customer Table View
            (<div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contact</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Transactions</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Volume</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Activity</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers?.map((customer) => (
                    <tr 
                      key={customer?.id}
                      className="border-b border-border hover:bg-muted/50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-foreground">{customer?.name}</div>
                          <div className="text-xs text-muted-foreground">{customer?.country}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm space-y-1">
                          <div className="text-foreground">{customer?.email}</div>
                          <div className="text-muted-foreground text-xs">{customer?.phone}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col space-y-1">
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadge(customer?.status)} inline-block w-fit`}>
                            {customer?.status}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getKycBadge(customer?.kycStatus)} inline-block w-fit`}>
                            {customer?.kycStatus?.replace('_', ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-medium text-foreground">{customer?.totalTransactions}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-medium text-foreground">${customer?.totalVolume?.toLocaleString()}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-muted-foreground">
                          {new Date(customer?.lastTransactionDate)?.toLocaleDateString()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleViewCustomer(customer)}
                            className="text-primary hover:text-primary/80 transition-colors"
                            title="View Details"
                          >
                            <Icon name="Eye" size={16} />
                          </button>
                          <button
                            className="text-info hover:text-info/80 transition-colors"
                            title="Send Message"
                          >
                            <Icon name="MessageSquare" size={16} />
                          </button>
                          <button
                            className="text-success hover:text-success/80 transition-colors"
                            title="Initiate Transfer"
                          >
                            <Icon name="Send" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>)
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-end">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerListModal;